import { auth, db, storage } from '../firebase';
import { 
  doc, 
  getDoc, 
  setDoc, 
  collection, 
  query, 
  where, 
  getDocs,
  serverTimestamp,
  writeBatch,
  updateDoc, 
  runTransaction
} from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { sendWelcomeEmail } from '../emails/emailService';
import { updateGroupMembershipsByEmail } from '../utils/userUpdateUtils';

export const createOrUpdateUserDocument = async (user) => {
  console.log('createOrUpdateUserDocument called for:', user.email, 'Stack trace:', new Error().stack);
  const userRef = doc(db, 'users', user.uid);

  try {
    return await runTransaction(db, async (transaction) => {
      const userDoc = await transaction.get(userRef);
      
      const isGoogleProvider = user.providerData?.some(p => p.providerId === 'google.com');

      if (!userDoc.exists()) {
        console.log('Creating new user document for:', user.email);
        const name = (user.name && String(user.name).trim())
                      || (user.displayName && String(user.displayName).trim())
                      || '';
        // photoURL is reserved for user-uploaded photos. Google's photo is stored
        // separately as googlePhotoURL so a Google sign-in never replaces a photo
        // the user uploaded to Gatherly. UserAvatar falls back to googlePhotoURL.
        const userData = {
          uid: user.uid,
          email: user.email,
          name,
          photoURL: isGoogleProvider ? '' : (user.photoURL || ''),
          createdAt: serverTimestamp(),
          isGuest: user.isGuest || false,
          isTestAccount: user.isTestAccount || false,
          welcomeEmailSent: false,
          ...(isGoogleProvider && {
            hasGoogleAuth: true,
            googlePhotoURL: user.photoURL || '',
          }),
        };
        transaction.set(userRef, userData);
        return { ...userData, isNewUser: true };
      } else {
        console.log('User document already exists for:', user.email);
        const existingData = userDoc.data();
        // Never overwrite existing name (e.g. if they set it in profile or from a previous RSVP)
        const name = (existingData.name && String(existingData.name).trim())
                      ? existingData.name
                      : (user.name && String(user.name).trim())
                          || (user.displayName && String(user.displayName).trim())
                          || '';
        // photoURL is owned by the user: never modify it on Google sign-in. Keep
        // Google's photo synced in googlePhotoURL (UserAvatar falls back to it).
        const photoURL = existingData.photoURL || '';
        const updatedData = {
          ...existingData,
          uid: user.uid,
          email: user.email,
          name,
          photoURL,
          isGuest: existingData.isGuest !== undefined ? existingData.isGuest : false,
          isTestAccount: existingData.isTestAccount || false,
          ...(isGoogleProvider && {
            hasGoogleAuth: true,
            googlePhotoURL: user.photoURL || existingData.googlePhotoURL || '',
          }),
        };
        transaction.update(userRef, updatedData);
        return { ...updatedData, isNewUser: false };
      }
    });
  } catch (error) {
    console.error('Error in createOrUpdateUserDocument:', error);
    throw error;
  }
};

export const sendWelcomeEmailIfNeeded = async (userData) => {
  // Always check the document in Firestore to avoid race conditions
  // Don't rely on the in-memory userData object which might be stale
  const userRef = doc(db, 'users', userData.uid || userData.id);
  const userDoc = await getDoc(userRef);
  
  if (!userDoc.exists()) {
    console.log('User document not found, skipping welcome email');
    return;
  }
  
  const currentData = userDoc.data();
  if (!currentData.welcomeEmailSent) {
    console.log('Sending welcome email for user:', userData.email);
    await sendWelcomeEmail(userData.email, userData.name || 'New Gatherly User');
    await updateDoc(userRef, { welcomeEmailSent: true });
  } else {
    console.log('Welcome email already sent for user:', userData.email);
  }
};

// Create a user when they sign up (e.g. RSVP as new user) or when linking an email to a new account.
// Name at sign-up is their profile name until they change it in Profile.
// For existing users who RSVP: we never update profile name from RSVP input – RSVP name is just for the RSVP record.
// shouldSendWelcomeEmail: true for RSVP flows, false for explicit sign-ups (they'll get it after code verification)
export const createUserDocument = async (email, name = '', shouldSendWelcomeEmail = false) => {
  const normalizedEmail = email ? email.toLowerCase().trim() : email;

  const userQuery = query(collection(db, 'users'), where('email', '==', normalizedEmail));
  const userSnapshot = await getDocs(userQuery);

  if (!userSnapshot.empty) {
    // Existing user – never overwrite profile name with RSVP/sign-up input; only set name if they don't have one
    const existingUserDoc = userSnapshot.docs[0];
    const existingUser = existingUserDoc.data();
    const hasExistingName = existingUser.name && String(existingUser.name).trim();

    if (shouldSendWelcomeEmail && !existingUser.welcomeEmailSent) {
      console.log('Sending welcome email for existing user from RSVP:', normalizedEmail);
      await sendWelcomeEmail(normalizedEmail, existingUser.name || name || 'New Gatherly User');
      await updateDoc(existingUserDoc.ref, { welcomeEmailSent: true });
    }

    if (name && !hasExistingName) {
      await updateDoc(existingUserDoc.ref, { name: name.trim() });
      return { ...existingUser, name: name.trim() };
    }
    return existingUser;
  }

  // Create a new user document - always a full user (no guest concept)
  const newUserRef = doc(collection(db, 'users'));
  const newUser = {
    uid: newUserRef.id,
    email: normalizedEmail,
    name: name || '',
    photoURL: '',
    isGuest: false, // Always false - no guest accounts
    isTestAccount: false,
    createdAt: serverTimestamp(),
    welcomeEmailSent: false,
  };

  await setDoc(newUserRef, newUser);
  
  // Send welcome email for RSVP flows (new users who RSVP get welcome email immediately)
  // For explicit sign-ups, welcome email is sent after code verification
  if (shouldSendWelcomeEmail) {
    console.log('Sending welcome email for new user from RSVP:', normalizedEmail);
    try {
      await sendWelcomeEmail(normalizedEmail, name || 'New Gatherly User');
      await updateDoc(newUserRef, { welcomeEmailSent: true });
      console.log('Welcome email sent successfully for:', normalizedEmail);
    } catch (error) {
      console.error('Error sending welcome email:', error);
      // Don't fail the RSVP if email fails, but log it
    }
  }
  
  return newUser;
};

// Legacy function - kept for backward compatibility
// All users are now full users from creation, so this just updates profile data
export const upgradeGuestUserToFull = async (uid, profileData = {}) => {
  const userRef = doc(db, 'users', uid);
  const userDoc = await getDoc(userRef);

  if (userDoc.exists()) {
    const existingData = userDoc.data();
    const updatedData = {
      ...existingData,
      ...profileData,
      isGuest: false, // Always ensure false
    };
    // Remove undefined values
    Object.keys(updatedData).forEach((key) => updatedData[key] === undefined && delete updatedData[key]);
    await setDoc(userRef, updatedData, { merge: true });
    return updatedData;
  }
  return null;
};

// Backward compatibility alias
export const createGuestUserDocument = createUserDocument;

// Function to handle RSVP for non-logged-in users
export const handleNonLoggedInRSVP = async (email, eventUrl) => {
  const userDoc = await getUserProfileByEmail(email);

  if (userDoc) {
    // Existing user: send sign-in code
    const result = await sendSignInCodeToExistingUser(email, eventUrl);
    return { shouldLogin: true, ...result };
  } else {
    // New user: create user profile and session
    // This is for RSVP flow, so send welcome email
    const newUser = await createUserDocument(email, '', true);
    if (newUser) {
      return { newUser, shouldCreateSession: true };
    }
  }
  return false;
};

// Function to upgrade a guest based on profile update
export const handleGuestUpgradeOnProfileUpdate = async (email, profileData) => {
  // Upgrade user only if they are currently a guest
  const isGuest = await isGuestUser(email);
  if (isGuest) {
    return await upgradeGuestUserToFull(email, profileData);
  }
  return false;
};

// Legacy function - kept for backward compatibility
// All users are now full users, so this always returns false
export const isGuestUser = async (uid) => {
  return false; // No guest accounts anymore
};

// Retrieve the user profile by email
export const getUserProfileByEmail = async (email) => {
  // Normalize email (lowercase, trim) to ensure consistent lookups
  const normalizedEmail = email ? email.toLowerCase().trim() : email;
  
  const usersRef = collection(db, 'users');
  const q = query(usersRef, where('email', '==', normalizedEmail));
  const querySnapshot = await getDocs(q);
  
  if (!querySnapshot.empty) {
    const doc = querySnapshot.docs[0];
    return { uid: doc.id, ...doc.data() };
  }
  
  return null;
};

// Function to handle test user creation and updates
export const createTestUserDocument = async (email, name) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, 'testpassword');
    const user = userCredential.user;

    const userRef = doc(db, 'users', user.uid);
    const userData = {
      uid: user.uid,
      email,
      name: name || '',
      photoURL: '',
      isGuest: false,  // Explicitly set to false
      isTestAccount: true,
      createdAt: serverTimestamp(),
    };

    await setDoc(userRef, userData);
    console.log('Test user created successfully:', userData);
    return userData;
  } catch (error) {
    console.error('Error creating test user:', error);
    throw error;
  }
};

// Send sign-in code to existing user (for RSVP flow)
export const sendSignInCodeToExistingUser = async (email, eventId) => {
  try {
    const { generateAuthCode } = await import('../auth/codeAuthService');
    const { sendAuthCodeEmail } = await import('../emails/emailService');
    
    const codeResult = await generateAuthCode(email, { isRSVP: true, eventId });
    if (!codeResult.success) {
      return { success: false, message: codeResult.error || 'Failed to generate code' };
    }
    
    const userDoc = await getUserProfileByEmail(email);
    const recipientName = (userDoc?.name || '').trim();
    const emailResult = await sendAuthCodeEmail(
      email, 
      recipientName, 
      codeResult.code, 
      false,
      eventId || null
    );
    
    if (emailResult.success) {
      return { success: true, message: 'Check your email for a sign-in code to complete your RSVP!', shouldLogin: true };
    } else {
      return { success: false, message: 'Failed to send code email' };
    }
  } catch (error) {
    console.error("Error sending sign-in code:", error);
    return { success: false, message: 'Error sending sign-in code: ' + error.message };
  }
};

// Update email in RSVPs
export const updateUserEmailInRSVPs = async (userId, newEmail) => {
  const rsvpsRef = collection(db, 'rsvps');
  const q = query(rsvpsRef, where('userId', '==', userId));
  const querySnapshot = await getDocs(q);

  const batch = writeBatch(db);
  querySnapshot.forEach((doc) => {
    batch.update(doc.ref, { email: newEmail });
  });

  await batch.commit();
};

// Update email in events
export const updateUserEmailInEvents = async (userId, newEmail) => {
  const eventsRef = collection(db, 'events');
  const q = query(eventsRef, where('createdBy', '==', userId));
  const querySnapshot = await getDocs(q);

  const batch = writeBatch(db);
  querySnapshot.forEach((doc) => {
    batch.update(doc.ref, { createdByEmail: newEmail });
  });

  await batch.commit();
};

// If the user has an uploaded avatar in Storage at avatars/{uid}, return its
// download URL. Returns null if there is no uploaded avatar (or on error).
export const getUploadedAvatarUrl = async (uid) => {
  if (!uid) return null;
  try {
    const avatarRef = ref(storage, `avatars/${uid}`);
    return await getDownloadURL(avatarRef);
  } catch (error) {
    if (error?.code !== 'storage/object-not-found') {
      console.warn('Error checking uploaded avatar:', error);
    }
    return null;
  }
};

// Self-heal photoURL: when the user has an uploaded avatar in Storage but their
// current photoURL points somewhere else (e.g. a legacy Google photo saved
// before we separated googlePhotoURL), restore photoURL to the Storage URL.
// Never runs when photoURL is empty/null (the user may have intentionally
// removed their avatar) to avoid resurrecting a deleted photo.
// Returns the updated userDoc (or the original if no change).
export const reclaimUploadedAvatar = async (userDoc) => {
  if (!userDoc || !userDoc.uid) return userDoc;
  if (!userDoc.photoURL) return userDoc;

  const uploadedUrl = await getUploadedAvatarUrl(userDoc.uid);
  if (!uploadedUrl || uploadedUrl === userDoc.photoURL) return userDoc;

  try {
    const userRef = doc(db, 'users', userDoc.uid);
    await updateDoc(userRef, { photoURL: uploadedUrl });
    console.log('Reclaimed uploaded avatar for user:', userDoc.email);
    return { ...userDoc, photoURL: uploadedUrl };
  } catch (error) {
    console.warn('Error reclaiming uploaded avatar:', error);
    return userDoc;
  }
};

// Function to handle user profile by UID
export const getUserProfileByUid = async (uid) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', uid));
    if (userDoc.exists()) {
      return { id: userDoc.id, ...userDoc.data() };
    }
    return null;
  } catch (error) {
    console.error('Error fetching user profile:', error);
    throw error;
  }
};
